'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/shadcn/dialog';
import { Input } from '@/components/shadcn/input';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { provisionNewTeam } from '@/utils/clerk/createTeam';
import { useOrganizationList } from '@clerk/nextjs';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

export const MAX_TEAMS_PER_USER = 5;

export default function CreateTeamDialog({
  isOpen,
  setIsOpen,
  currentTeamCount
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentTeamCount: number;
}) {
  const { createOrganization, setActive, userMemberships } =
    useOrganizationList({ userMemberships: true });
  const router = useRouter();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const atLimit = currentTeamCount >= MAX_TEAMS_PER_USER;

  const handleClose = (open: boolean) => {
    if (submitting) return;
    setIsOpen(open);
    if (!open) {
      setName('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('Team name must be at least 2 characters.');
      return;
    }
    if (atLimit) {
      setError(
        `You've reached the limit of ${MAX_TEAMS_PER_USER} teams per account.`
      );
      return;
    }
    if (!createOrganization || !setActive) return;

    setSubmitting(true);
    try {
      const org = await createOrganization({ name: trimmed });
      await provisionNewTeam(org.id);
      await userMemberships?.revalidate?.();
      await setActive({ organization: org.id });
      toast({ title: 'Team created' });
      setIsOpen(false);
      setName('');
      router.push('/account/');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create team.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create a Team</DialogTitle>
            <DialogDescription>
              Create a team to collaborate on interviews, share billing, and
              unlock higher usage limits.
            </DialogDescription>
          </DialogHeader>
          <div className="my-6 space-y-2">
            <Input
              id="team-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Team name"
              maxLength={80}
              disabled={submitting || atLimit}
            />
            {atLimit && (
              <p className="text-sm text-muted-foreground">
                You've reached the limit of {MAX_TEAMS_PER_USER} teams per
                account.
              </p>
            )}
            {error && !atLimit && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || atLimit || name.trim().length < 2}
            >
              {submitting ? 'Creating…' : 'Create Team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
